// common
import React from 'react';
// icon
import { FaRegBell } from 'react-icons/fa';

import { ReactComponent as NewAlarmSVG } from 'assets/images/menu/menu_icon_alarm_new.svg';
// style
import styled from 'styled-components';

// components
import PopUpButton from 'components/button/PopUpButton';

// hooks
import useAlarm from 'hooks/alarm/useAlarm';

// utils
import { formatDateDisplayInfo } from 'utils/date/convertDateUtil';

import { scrollBar } from 'styles/commons/scrollBar';
import { text } from 'styles/commons/text';

const AlarmPopUpContent = () => {
  // 알림
  const {
    notiInfo,
    onClickMarkAllAsRead,
    onClickNavigateToNotiLink,
    visiblePopupButton,
    openPopupButton,
    closePopupButton,
  } = useAlarm();

  return (
    <PopUpButton
      icon={
        !notiInfo.isAllRead ? (
          <>
            <NewAlarmSVG
              style={{
                width: '24px',
                height: '26px',
              }}
            />
            <NewAlarmData>{notiInfo.list.length}</NewAlarmData>
          </>
        ) : (
          <FaRegBell
            style={{
              width: '22px',
              height: '22px',
            }}
          />
        )
      }
      onClick={() => {
        openPopupButton('noti');
      }}
      onClose={closePopupButton}
      className="alram_popup_btn"
      aria-label={notiInfo.isAllRead ? '모든 알림 읽음' : '새로운 알림 있음'}
      isopen={visiblePopupButton.isopen && visiblePopupButton.type === 'noti'}
    >
      <NotiInfoArea>
        {notiInfo.list.length === 0 ? (
          // 알림이 없는 경우
          <NotiInfoArea>
            <div className="alarm_list">
              {/* 알림 리시트 아이템 영역 */}
              <NoAlarmItem className="alarm_item none">
                <AlarmNoneCont>새로운 알림이 없습니다.</AlarmNoneCont>
              </NoAlarmItem>
            </div>
          </NotiInfoArea>
        ) : (
          // 알림이 있는 경우
          <NotiInfoArea>
            {/* 알림 헤더 영역 */}
            <div className="alarm_header">
              <h3 className="alarm_title">알림</h3>
            </div>
            {/* 알림 리스트 영역 */}
            <div className="alarm_list">
              {/* 알림 리시트 아이템 영역 */}
              {notiInfo.list.map((item, idx) => (
                <AlarmItem
                  className="alarm_item"
                  key={idx.toString()}
                  onClick={() => onClickNavigateToNotiLink(item.link)}
                  style={{
                    cursor: item.link ? 'pointer' : 'auto',
                  }}
                >
                  <AlarmCont className="alarm_cont">
                    <em className="alarm_type">
                      {renderNotiTypeMessage(item.notificationType)}
                    </em>
                    <div className="alarm_message">
                      {/* 백틱(`) 사이의 텍스트를 볼드 처리하는 함수 호출 */}
                      {renderBoldNotiMessage(item.content)}
                    </div>
                    <p className="alarm_date">
                      {formatDateDisplayInfo(item.createdAt)}
                    </p>
                  </AlarmCont>
                </AlarmItem>
              ))}
              <span className="alarm_msg">
                확인하지 않은 알림은 30일 이후에 자동 삭제됩니다.
              </span>
            </div>
            {/* 알림 푸터 영역 */}
            <AlarmFooter className="alarm_footer">
              <text.Link
                className="alarm_is_all_read"
                onClick={() => {
                  if (!notiInfo.isAllRead) {
                    return onClickMarkAllAsRead();
                  }
                }}
              >
                모두 읽음 표시
              </text.Link>
            </AlarmFooter>
          </NotiInfoArea>
        )}
      </NotiInfoArea>
    </PopUpButton>
  );
};

const renderNotiTypeMessage = (type) => {
  switch (type) {
    case 'NOTICE':
      return '공지 사항';
    case 'ACCOUNT':
      return '계정 정보';
    case 'COMMUNITY':
    case 'COMMUNITY_REPLY':
      return '이용 알림';
    case 'APPLICATION':
    case 'PAYMENT':
    case 'PRICE':
      return '상품 이용';
    default:
      return type;
  }
};

const renderBoldNotiMessage = (message) => {
  // 백틱을 기준으로 문자열을 분리하고 빈 값을 필터링
  const parts = message.split(/(`.*?`)/).filter((part) => part.trim() !== '');

  return parts.map((part, index) =>
    part.startsWith('`') && part.endsWith('`') ? (
      <strong key={index}>{part.slice(1, -1)}</strong> // 백틱 제거 후 볼드 처리
    ) : (
      <span key={index}>{part}</span>
    )
  );
};

export default AlarmPopUpContent;
// 알림이 없는 경우
const AlarmNoneCont = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50px;
  font-weight: 400;
`;
const NotiInfoArea = styled.div`
  background: ${({ theme }) => theme.color.white};
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  max-width: 340px;
  width: 340px;
  font-size: 14px;
  border-radius: 6px;
  overflow: hidden;

  .alarm_msg {
    display: inline-block;
    width: 100%;
    padding: 12px 18px;
    font-size: 13px;
    font-weight: 400;
    line-height: 1;
    text-align: center;
    color: ${({ theme }) => theme.color.gray800};
  }

  .alarm_header {
    padding: 16px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid ${({ theme }) => theme.color.gray100};

    .alarm_title {
      font-size: 15px;
      font-weight: 500;
      line-height: 1;
      color: ${({ theme }) => theme.color.gray900};
    }
  }

  .alarm_list {
    max-height: 280px;
    ${scrollBar.default}

    .alarm_item {
      padding: 12px 18px;
      border-bottom: 1px solid ${({ theme }) => theme.color.gray100};

      &:last-child {
        border-bottom: 0;
      }
      &:hover {
        background: ${({ theme }) => theme.color.gray80};
      }

      &.none {
        &:hover {
          background: none;
        }
      }
    }
  }
`;

const NoAlarmItem = styled.div`
  &:hover {
    background: none;
  }
`;
const NewAlarmData = styled.div`
  position: absolute;
  right: -3px;
  top: 3px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  background: ${({ theme }) => theme.color.red600};
  border-radius: 8px;
  line-height: 16px;
  text-align: center;
`;
const AlarmItem = styled.div`
  padding: 10px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray100};

  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: ${({ theme }) => theme.color.gray80};
  }
`;

const AlarmCont = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.gray900};

  .alarm_type {
    display: inline-block;
    color: ${({ theme }) => theme.color.gray800};
    font-weight: 600;
  }
  .alarm_date {
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme.color.gray500};
  }

  .alarm_message {
    strong {
      font-weight: 500;
    }
    span {
      color: ${({ theme }) => theme.color.gray800};
      font-weight: 300;
    }
  }
`;

const AlarmFooter = styled.div`
  padding: 12px 18px;
  background: ${({ theme }) => theme.color.gray80};
  border-top: 1px solid ${({ theme }) => theme.color.gray100};

  .alarm_is_all_read {
    font-size: 14px;
    text-decoration: none;
  }
`;
